import React, { useState, useEffect } from 'react';
import { FaMoneyBillWave, FaCreditCard, FaHistory, FaFileInvoice, FaDownload, FaSpinner, FaPlus, FaTimes } from 'react-icons/fa';
import { useSubscription } from '../../hooks/useSubscription';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../hooks/useToast';
import api from '../../utils/api';
import SubscriptionDetails from '../dashboard/SubscriptionDetails';
import LoadingSpinner from '../common/LoadingSpinner';
import Modal from '../common/Modal';

/**
 * School Billing component for managing subscriptions, payments, and invoices
 */
const SchoolBilling = () => {
  const { user } = useAuth();
  const { loading: subscriptionLoading } = useSubscription();
  const { showSuccess, showError } = useToast();
  
  // State for billing history
  const [billingHistory, setBillingHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);
  const [activeTab, setActiveTab] = useState('subscription');
  
  // Payment method modal states
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [paymentType, setPaymentType] = useState('card');
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    cardHolder: '',
    expiryDate: '',
    cvv: ''
  });
  const [phoneNumber, setPhoneNumber] = useState('');
  const [savingPaymentMethod, setSavingPaymentMethod] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);
  
  useEffect(() => {
    // Fetch billing history and payment methods
    const fetchBillingData = async () => {
      try {
        await Promise.all([
          fetchBillingHistory(),
          fetchPaymentMethods()
        ]);
      } catch (error) {
        console.error('Error fetching billing data:', error);
      }
    };
    
    fetchBillingData();
  }, []);
  
  // Fetch billing/payment history
  const fetchBillingHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await api.get('/subscriptions/history');
      setBillingHistory(response.data.data || []);
    } catch (error) {
      console.error('Error fetching billing history:', error);
      showError('Failed to load billing history');
      setBillingHistory([]); // Initialize with empty array instead of placeholder data
    } finally {
      setLoadingHistory(false);
    }
  };
  
  // Fetch saved payment methods
  const fetchPaymentMethods = async () => {
    try {
      setLoadingPaymentMethods(true);
      const response = await api.get('/subscriptions/payment-methods');
      setPaymentMethods(response.data.data || []);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
      showError('Failed to load payment methods');
      setPaymentMethods([]); // Initialize with empty array instead of placeholder data
    } finally {
      setLoadingPaymentMethods(false);
    }
  };
  
  // Download invoice
  const downloadInvoice = async (transactionId) => {
    try {
      setProcessingAction(true);
      // Get invoice PDF from API
      const response = await api.get(`/invoices/${transactionId}`, {
        responseType: 'blob' // Important for downloading files
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${transactionId}.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showSuccess(`Invoice ${transactionId} downloaded`);
    } catch (error) {
      console.error('Error downloading invoice:', error);
      showError('Failed to download invoice');
    } finally {
      setProcessingAction(false);
    }
  };
  
  // Set payment method as default
  const handleSetDefaultPaymentMethod = async (methodId) => {
    try {
      setProcessingAction(true);
      const response = await api.put(`/subscriptions/payment-methods/${methodId}/default`);
      if (response.data.success) {
        showSuccess('Payment method set as default');
        await fetchPaymentMethods(); // Refresh the list
      }
    } catch (error) {
      console.error('Error setting default payment method:', error);
      showError('Failed to set default payment method');
    } finally {
      setProcessingAction(false);
    }
  };
  
  // Remove payment method
  const handleRemovePaymentMethod = async (methodId) => {
    try {
      setProcessingAction(true);
      const response = await api.delete(`/subscriptions/payment-methods/${methodId}`);
      if (response.data.success) {
        showSuccess('Payment method removed');
        await fetchPaymentMethods(); // Refresh the list
      }
    } catch (error) {
      console.error('Error removing payment method:', error);
      showError('Failed to remove payment method');
    } finally {
      setProcessingAction(false);
    }
  };
  
  // Handle card details change
  const handleCardDetailsChange = (e) => {
    const { name, value } = e.target;
    setCardDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Format card number with spaces
  const formatCardNumber = (value) => {
    return value
      .replace(/\s/g, '')
      .replace(/(\d{4})/g, '$1 ')
      .trim();
  };
  
  // Handle card number input with formatting
  const handleCardNumberChange = (e) => {
    const input = e.target.value.replace(/\D/g, '').substring(0, 16);
    setCardDetails(prev => ({
      ...prev,
      cardNumber: formatCardNumber(input)
    }));
  };
  
  // Format expiry date
  const handleExpiryDateChange = (e) => {
    let input = e.target.value;
    input = input.replace(/\D/g, '');
    
    if (input.length > 2) {
      input = `${input.substring(0, 2)}/${input.substring(2, 4)}`;
    }
    
    setCardDetails(prev => ({
      ...prev,
      expiryDate: input
    }));
  };
  
  // Handle phone number input 
  const handlePhoneNumberChange = (e) => {
    // Allow only numbers and + sign for international format
    const input = e.target.value.replace(/[^\d+]/g, '');
    setPhoneNumber(input);
  };
  
  // Add new payment method
  const handleAddPaymentMethod = async () => {
    try {
      setSavingPaymentMethod(true);
      
      if (paymentType === 'card') {
        // Validate card details
        if (!cardDetails.cardNumber || !cardDetails.cardHolder || !cardDetails.expiryDate || !cardDetails.cvv) {
          showError('Please fill in all card details');
          return;
        }
        
        // Check for test card in development mode
        const cleanCardNumber = cardDetails.cardNumber.replace(/\s/g, '');
        const isTestCard = process.env.NODE_ENV === 'development' && 
          cleanCardNumber === '4403933211508236' &&
          cardDetails.cardHolder === 'Edgar Temba' &&
          cardDetails.expiryDate === '08/25' &&
          cardDetails.cvv === '608';
          
        if (isTestCard) {
          console.log('Using test card - payment method will be added successfully');
        }
        
        // In a real app, you would tokenize the card with a payment gateway
        // before sending to your server
        const last4 = cleanCardNumber.slice(-4);
        
        await api.post('/subscriptions/payment-methods', {
          type: 'card',
          cardType: getCardType(cleanCardNumber),
          last4,
          cardHolder: cardDetails.cardHolder,
          expiryDate: cardDetails.expiryDate,
          isTestCard // Add flag for server to know this is a test card
          // Don't send CVV to server for security
        });
        
      } else if (paymentType === 'mpesa') {
        // Validate phone number
        if (!phoneNumber) {
          showError('Please enter a phone number');
          return;
        }
        
        await api.post('/subscriptions/payment-methods', {
          type: 'mpesa',
          phoneNumber
        });
      }
      
      showSuccess('Payment method added successfully');
      resetPaymentForm();
      setShowAddPaymentModal(false);
      await fetchPaymentMethods(); // Refresh the list
      
    } catch (error) {
      console.error('Error adding payment method:', error);
      showError('Failed to add payment method');
    } finally {
      setSavingPaymentMethod(false);
    }
  };
  
  // Reset payment form
  const resetPaymentForm = () => {
    setPaymentType('card');
    setCardDetails({
      cardNumber: '',
      cardHolder: '',
      expiryDate: '',
      cvv: ''
    });
    setPhoneNumber('');
  };
  
  // Get card type based on first digits
  const getCardType = (number) => {
    const firstDigit = number[0];
    const firstTwoDigits = number.substring(0, 2);
    
    if (firstDigit === '4') return 'visa';
    if (['51', '52', '53', '54', '55'].includes(firstTwoDigits)) return 'mastercard';
    if (firstTwoDigits === '34' || firstTwoDigits === '37') return 'amex';
    return 'unknown';
  };
  
  // Format amount as currency
  const formatCurrency = (amount) => {
    return `KES ${amount.toLocaleString()}`;
  };
  
  // Format date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold text-gray-800 mb-1">Billing & Payments</h1>
        <p className="text-gray-600">Manage your subscription, billing history, and payment methods</p>
      </div>
      
      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('subscription')}
            className={`${
              activeTab === 'subscription'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Subscription
          </button>
          <button
            onClick={() => setActiveTab('billing')}
            className={`${
              activeTab === 'billing'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Billing History
          </button>
          <button
            onClick={() => setActiveTab('payment')}
            className={`${
              activeTab === 'payment'
                ? 'border-primary text-primary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`}
          >
            Payment Methods
          </button>
        </nav>
      </div>
      
      {/* Tab Content */}
      <div className="mt-6">
        {/* Subscription Tab */}
        {activeTab === 'subscription' && (
          <div>
            {subscriptionLoading ? (
              <div className="bg-white rounded-lg shadow-md p-8 flex justify-center">
                <LoadingSpinner size="lg" />
              </div>
            ) : (
              <SubscriptionDetails />
            )}
          </div>
        )}
        
        {/* Billing History Tab */}
        {activeTab === 'billing' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-800">Billing History</h2>
              <p className="text-sm text-gray-500 mt-1">
                View all your past transactions and download invoices
              </p>
            </div>
            
            {loadingHistory ? (
              <div className="p-8 flex justify-center">
                <LoadingSpinner size="lg" />
              </div>
            ) : billingHistory.length === 0 ? (
              <div className="p-8 text-center">
                <FaHistory className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-800 mb-1">No billing history</h3>
                <p className="text-gray-500">
                  Your payment history will appear here once you've made a payment
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Invoice
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {billingHistory.map((invoice) => (
                      <tr key={invoice._id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {invoice.transactionId}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(invoice.date)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {invoice.description}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatCurrency(invoice.amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                            invoice.status === 'paid' 
                              ? 'bg-green-100 text-green-800' 
                              : invoice.status === 'pending' 
                                ? 'bg-yellow-100 text-yellow-800' 
                                : 'bg-red-100 text-red-800'
                          }`}>
                            {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => downloadInvoice(invoice.transactionId)}
                            disabled={processingAction}
                            className="text-primary hover:text-primary-dark flex items-center justify-end disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {processingAction ? (
                              <FaSpinner className="animate-spin mr-1" />
                            ) : (
                              <FaDownload className="mr-1" />
                            )} 
                            Download
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        
        {/* Payment Methods Tab */}
        {activeTab === 'payment' && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-medium text-gray-800">Payment Methods</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Manage your saved payment methods
                  </p>
                </div>
                <button 
                  onClick={() => {
                    resetPaymentForm();
                    setShowAddPaymentModal(true);
                  }}
                  className="btn-primary text-sm flex items-center"
                >
                  <FaPlus className="mr-1" /> Add Payment Method
                </button>
              </div>
            </div>
            
            {loadingPaymentMethods ? (
              <div className="p-8 flex justify-center">
                <LoadingSpinner size="lg" />
              </div>
            ) : paymentMethods.length === 0 ? (
              <div className="p-8 text-center">
                <FaCreditCard className="mx-auto h-12 w-12 text-gray-300 mb-4" />
                <h3 className="text-lg font-medium text-gray-800 mb-1">No payment methods</h3>
                <p className="text-gray-500 mb-6">
                  You haven't added any payment methods yet
                </p>
                <button 
                  onClick={() => {
                    resetPaymentForm();
                    setShowAddPaymentModal(true);
                  }}
                  className="btn-primary"
                >
                  Add Payment Method
                </button>
              </div>
            ) : (
              <div className="p-6">
                <div className="space-y-4">
                  {paymentMethods.map((method) => (
                    <div 
                      key={method._id} 
                      className="border border-gray-200 rounded-lg p-4 flex justify-between items-center"
                    >
                      <div className="flex items-center">
                        {method.type === 'card' ? (
                          <>
                            <div className="bg-blue-100 p-3 rounded-full">
                              <FaCreditCard className="text-blue-600" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {method.cardType.charAt(0).toUpperCase() + method.cardType.slice(1)} •••• {method.last4}
                              </div>
                              <div className="text-sm text-gray-500">
                                Expires {method.expiryDate}
                              </div>
                              {method.cardHolder && (
                                <div className="text-sm text-gray-500">
                                  {method.cardHolder}
                                </div>
                              )}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="bg-green-100 p-3 rounded-full">
                              <FaMoneyBillWave className="text-green-600" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                M-Pesa
                              </div>
                              <div className="text-sm text-gray-500">
                                {method.phoneNumber}
                              </div>
                            </div>
                          </>
                        )}
                        
                        {method.isDefault && (
                          <span className="ml-4 px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Default
                          </span>
                        )}
                      </div>
                      
                      <div className="flex space-x-2">
                        {!method.isDefault && (
                          <button 
                            onClick={() => handleSetDefaultPaymentMethod(method._id)}
                            disabled={processingAction}
                            className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Set as Default
                          </button>
                        )}
                        <button 
                          onClick={() => handleRemovePaymentMethod(method._id)}
                          disabled={processingAction || (method.isDefault && paymentMethods.length > 1)}
                          className="text-sm text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Additional Help Section */}
      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-lg font-medium text-gray-800 mb-4">Need Help with Billing?</h2>
        <p className="text-gray-600 mb-4">
          If you have any questions or concerns about your billing or subscription, our support team is here to help.
        </p>
        <div className="flex space-x-4">
          <a 
            href="mailto:billing@meira.edu" 
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Contact Billing Support
          </a>
          <a 
            href="/faqs#billing" 
            className="px-4 py-2 text-primary hover:underline"
          >
            View Billing FAQs
          </a>
        </div>
      </div>
      
      {/* Add Payment Method Modal */}
      <Modal
        isOpen={showAddPaymentModal}
        onClose={() => !savingPaymentMethod && setShowAddPaymentModal(false)}
        title="Add Payment Method"
        size="md"
      >
        <div className="p-6">
          <div className="mb-6">
            <div className="grid grid-cols-2 gap-3 mb-6">
              <button
                type="button"
                onClick={() => setPaymentType('card')}
                className={`p-3 border rounded-md text-center ${
                  paymentType === 'card' 
                    ? 'border-primary bg-primary bg-opacity-5' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="flex justify-center mb-2">
                  <FaCreditCard className="text-xl" />
                </div>
                <div className="font-medium">Credit/Debit Card</div>
              </button>
              <button
                type="button"
                onClick={() => setPaymentType('mpesa')}
                className={`p-3 border rounded-md text-center ${
                  paymentType === 'mpesa' 
                    ? 'border-primary bg-primary bg-opacity-5' 
                    : 'border-gray-300 hover:border-gray-400'
                }`}
              >
                <div className="flex justify-center mb-2">
                  <FaMoneyBillWave className="text-xl" />
                </div>
                <div className="font-medium">M-Pesa</div>
              </button>
            </div>
            
            {paymentType === 'card' ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Card Number
                  </label>
                  <input
                    type="text"
                    name="cardNumber"
                    value={cardDetails.cardNumber}
                    onChange={handleCardNumberChange}
                    placeholder="1234 5678 9012 3456"
                    className="form-input w-full"
                    maxLength="19" // 16 digits + 3 spaces
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cardholder Name
                  </label>
                  <input
                    type="text"
                    name="cardHolder"
                    value={cardDetails.cardHolder}
                    onChange={handleCardDetailsChange}
                    placeholder="John Doe"
                    className="form-input w-full"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Expiry Date
                    </label>
                    <input
                      type="text"
                      name="expiryDate"
                      value={cardDetails.expiryDate}
                      onChange={handleExpiryDateChange}
                      placeholder="MM/YY"
                      className="form-input w-full"
                      maxLength="5" // MM/YY format
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      CVV
                    </label>
                    <input
                      type="password"
                      name="cvv"
                      value={cardDetails.cvv}
                      onChange={handleCardDetailsChange}
                      placeholder="123"
                      className="form-input w-full"
                      maxLength="4" // 3-4 digits
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  M-Pesa Phone Number
                </label>
                <input
                  type="text"
                  value={phoneNumber}
                  onChange={handlePhoneNumberChange}
                  placeholder="+254 712 345 678"
                  className="form-input w-full"
                />
                <p className="mt-2 text-sm text-gray-500">
                  Please enter the phone number registered with M-Pesa
                </p>
              </div>
            )}
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              onClick={() => setShowAddPaymentModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              disabled={savingPaymentMethod}
            >
              Cancel
            </button>
            <button
              onClick={handleAddPaymentMethod}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
              disabled={savingPaymentMethod}
            >
              {savingPaymentMethod ? (
                <span className="flex items-center">
                  <FaSpinner className="animate-spin mr-2" /> Saving...
                </span>
              ) : (
                'Save Payment Method'
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SchoolBilling; 